import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Cancel,
  Download,
  Add,
  Search,
  Hotel,
  CheckCircle,
  AccessTime,
  CancelOutlined,
} from '@mui/icons-material';
import CustomerBookingDialog from '../components/CustomerBookingDialog';

const initialBookings = [
  {
    id: 'BK2026',
    roomType: 'Deluxe',
    roomNumber: 'DEL-204',
    checkIn: '2025-11-20',
    checkOut: '2025-11-23',
    status: 'Upcoming',
    totalAmount: 540,
    nights: 3,
    guests: 2,
    bookingDate: '2025-11-10',
  },
  {
    id: 'BK2025',
    roomType: 'Suite',
    roomNumber: 'SUI-305',
    checkIn: '2025-10-15',
    checkOut: '2025-10-18',
    status: 'Completed',
    totalAmount: 840,
    nights: 3,
    guests: 2,
    bookingDate: '2025-10-01',
  },
  {
    id: 'BK2024',
    roomType: 'Standard',
    roomNumber: 'STD-102',
    checkIn: '2025-09-05',
    checkOut: '2025-09-08',
    status: 'Completed',
    totalAmount: 360,
    nights: 3,
    guests: 1,
    bookingDate: '2025-08-25',
  },
];

export default function CustomerBookings() {
  const [bookings, setBookings] = useState(initialBookings);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const statusFilters = ['All', 'Upcoming', 'Completed', 'Cancelled'];

  const filteredBookings = bookings.filter((booking) => {
    const matchesSearch = booking.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          booking.roomType.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 0 || booking.status === statusFilters[activeTab];
    return matchesSearch && matchesTab;
  });

  const handleMenuClick = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleViewBooking = () => {
    setViewDialogOpen(true);
    setAnchorEl(null);
  };

  const handleCancelBooking = () => {
    if (selectedBooking && selectedBooking.status === 'Upcoming') {
      setBookings(bookings.map(b => 
        b.id === selectedBooking.id ? { ...b, status: 'Cancelled' } : b
      ));
      setSnackbar({
        open: true,
        message: `Booking ${selectedBooking.id} cancelled successfully!`,
        severity: 'success',
      });
    }
    setAnchorEl(null);
  };

  const handleSaveBooking = (bookingData) => {
    setBookings([bookingData, ...bookings]);
    setSnackbar({
      open: true,
      message: `Booking confirmed! Booking ID: ${bookingData.id}`,
      severity: 'success',
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Upcoming': return <AccessTime />;
      case 'Completed': return <CheckCircle />;
      case 'Cancelled': return <CancelOutlined />;
      default: return <Hotel />;
    }
  };

  const stats = [
    { 
      label: 'Total Bookings', 
      value: bookings.length, 
      color: '#1976d2',
      icon: Hotel,
    },
    { 
      label: 'Upcoming', 
      value: bookings.filter(b => b.status === 'Upcoming').length, 
      color: '#ed6c02',
      icon: AccessTime,
    },
    { 
      label: 'Completed', 
      value: bookings.filter(b => b.status === 'Completed').length, 
      color: '#2e7d32',
      icon: CheckCircle,
    },
    { 
      label: 'Total Spent', 
      value: `$${bookings.filter(b => b.status === 'Completed').reduce((sum, b) => sum + b.totalAmount, 0)}`, 
      color: '#9c27b0',
      icon: Hotel,
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            My Bookings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track all your hotel reservations
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => setBookingDialogOpen(true)}
        >
          New Booking
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            {statusFilters.map((filter, index) => (
              <Tab key={filter} label={filter} />
            ))}
          </Tabs>
        </Box>
        <Box p={2}>
          <TextField
            fullWidth
            placeholder="Search by booking ID or room type..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Booking ID</strong></TableCell>
              <TableCell><strong>Room</strong></TableCell>
              <TableCell><strong>Check-in</strong></TableCell>
              <TableCell><strong>Check-out</strong></TableCell>
              <TableCell><strong>Nights</strong></TableCell>
              <TableCell><strong>Guests</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBookings.map((booking) => (
              <TableRow key={booking.id} hover>
                <TableCell><strong>{booking.id}</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                      {getStatusIcon(booking.status)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {booking.roomType}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {booking.roomNumber}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{new Date(booking.checkIn).toLocaleDateString()}</TableCell>
                <TableCell>{new Date(booking.checkOut).toLocaleDateString()}</TableCell>
                <TableCell>{booking.nights}</TableCell>
                <TableCell>{booking.guests}</TableCell>
                <TableCell><strong>${booking.totalAmount}</strong></TableCell>
                <TableCell>
                  <Chip
                    label={booking.status}
                    size="small"
                    color={
                      booking.status === 'Upcoming' ? 'primary' : 
                      booking.status === 'Completed' ? 'success' : 'error'
                    }
                  />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={(e) => handleMenuClick(e, booking)}>
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleViewBooking}>
          <Visibility fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)}>
          <Download fontSize="small" sx={{ mr: 1 }} />
          Download Receipt
        </MenuItem>
        {selectedBooking?.status === 'Upcoming' && (
          <MenuItem onClick={handleCancelBooking} sx={{ color: 'error.main' }}>
            <Cancel fontSize="small" sx={{ mr: 1 }} />
            Cancel Booking
          </MenuItem>
        )}
      </Menu>

      <CustomerBookingDialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        onSave={handleSaveBooking}
      />

      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Hotel color="primary" />
            <Typography variant="h6">Booking Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.id}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedBooking.status}
                  size="small"
                  color={
                    selectedBooking.status === 'Upcoming' ? 'primary' : 
                    selectedBooking.status === 'Completed' ? 'success' : 'error'
                  }
                />
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Room Type</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.roomType}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Room Number</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedBooking.roomNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Check-in</Typography>
                <Typography variant="body1">{new Date(selectedBooking.checkIn).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Check-out</Typography>
                <Typography variant="body1">{new Date(selectedBooking.checkOut).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Nights</Typography>
                <Typography variant="body1">{selectedBooking.nights}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Guests</Typography>
                <Typography variant="body1">{selectedBooking.guests}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Booking Date</Typography>
                <Typography variant="body1">{new Date(selectedBooking.bookingDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Total Amount</Typography>
                  <Typography variant="h6" color="primary">${selectedBooking.totalAmount}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Download />}>
            Download Receipt
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
